import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, filter, Subscription, take } from "rxjs";
import {
  Aangifte,
  BokkzAlgDataService,
  BokkzIbDataService,
  IncomeTaxEntity, JaarrekeningBalans
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormBuilder, FormGroup} from "@angular/forms";
import {Event, NavigationEnd, Router} from "@angular/router";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiDetailsModule} from "@snelstart/ui-details";

@Component({
  selector:
    'snelstart-venture-fiscaal-feature-spec-overig-materiele-vaste-activa',
  standalone: true,
  imports: [CommonModule, SharedUiButtonModule, SharedUiDialogModule, SharedUiFormControlsModule, SharedUiDetailsModule],
  templateUrl: './spec-overig-materiele-vaste-activa.component.html',
  styleUrl: './spec-overig-materiele-vaste-activa.component.scss',
})
export class SpecOverigMaterieleVasteActivaComponent implements OnInit,OnDestroy {
  private routerSubscription: Subscription | undefined;
  private incomeTaxEntitySubscription: Subscription | undefined;
  private clientEntitySubscription: Subscription | undefined;
  private isSaving = false;
  @Input() public id!: string;
  public clientId!: string;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public partnerBoolean = {} as boolean;
  @Input() partner = new BehaviorSubject<boolean>(false);

  public showSpinner = true;

  public materieleVasteActivaOverigFg = {} as FormGroup;
  public dialogVisible = false;

  showDialog() {
    this.dialogVisible = true;
  }

  constructor(
      public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
      private router: Router,
      private fb: FormBuilder) {
    this.materieleVasteActivaOverigFg = this.fb.group<JaarrekeningBalans>({
      omschrijving: 'andereVasteActiva',
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      aanschafWaarde: null as unknown as number,
      restWaarde: null as unknown as number,
      bodemwaarde: 0,
    });
  }

  private resetFormGroup() {
    this.materieleVasteActivaOverigFg.setValue({
      omschrijving: 'andereVasteActiva',
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      aanschafWaarde: null as unknown as number,
      restWaarde: null as unknown as number,
      bodemwaarde: 0,
    }, {emitEvent: false})
  }


  ngOnInit(): void {
    this.incomeTaxEntitySubscription =this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      if (incomeTaxEntity.aangifte) {
        this.partner.subscribe((partner) => {
          this.partnerBoolean = partner;
          this.resetFormGroup()
          const onderneming = !partner ? this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0] : this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0];
          const overig =   onderneming?.materieleVasteActiva?.find(item => item.omschrijving === 'andereVasteActiva') as JaarrekeningBalans;
          this.materieleVasteActivaOverigFg.patchValue(overig);
        })
      }
    })
    this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) =>{
      this.clientId=client.sstRelationId!;
    })
    this.routerSubscription = this.router.events
        .pipe(
            filter(
                (event: Event): event is NavigationEnd =>
                    event instanceof NavigationEnd,
            ),
        )
        .subscribe((event: NavigationEnd) => {
          this.handleUrlChange(event.urlAfterRedirects);
        });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
    if (this.incomeTaxEntitySubscription) {
      this.incomeTaxEntitySubscription.unsubscribe();
    }
    if (this.clientEntitySubscription) {
      this.clientEntitySubscription.unsubscribe();
    }
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.dataService.postIncomeTaxEntity(this.clientId,false, this.incomeTaxEntity);
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.materieleVasteActivaOverigFg.dirty
  }

  saveForm(): void {
    this.partner.pipe(take(1)).subscribe((partner) => {
      const ondernemingen = !partner
        ? this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen
        : this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen;
      if (ondernemingen) {
        const machines = ondernemingen[0]?.materieleVasteActiva?.find(item => item.omschrijving === 'andereVasteActiva');
        if (machines) {
          Object.assign(machines, this.materieleVasteActivaOverigFg.getRawValue());
          machines.bodemwaarde=0;
        }
        this.incomeTaxEntity.aangifte = {
          ...this.incomeTaxEntity.aangifte,
          ...(partner &&
            { partnerAangifte:
                { ...this.incomeTaxEntity.aangifte?.partnerAangifte } }),
        } as Aangifte;

        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      }
    });
  }

  public heeftOndernemingen(): boolean {
    if (this.partnerBoolean) {
      return !!(this.incomeTaxEntity &&
        this.incomeTaxEntity.aangifte?.partnerAangifte &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1 &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen.length > 0);
    } else {
      return !!(this.incomeTaxEntity &&
        this.incomeTaxEntity.aangifte?.box1 &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen.length > 0);
    }
  }
}


